import React from "react";
import { IconType } from "react-icons";
import TrendUpIcon from "../../assets/svgs/trend-up.svg";
import TrendDownIcon from "../../assets/svgs/trend-down.svg";
import WalletIcon from "../../assets/svgs/empty-wallet.svg";
import { ArrowDown, ArrowUp } from "iconsax-react";

interface SummaryCardProps {
  className?: string;
  titleHeading?: string;
  totalHeading?: string | number | null;
  sectionOneTitle?: string;
  sectionOneCount?: string | number | null;
  sectionOneTotal?: string | number | null;
  sectionTwoTitle?: string;
  sectionTwoCount?: string | number | null;
  sectionTwoTotal?: string | number | null;
  sectionThreeTitle?: string;
  sectionThreeCount?: string | number | null;
  sectionThreeTotal?: string | number | null;
  isIcon?: boolean;
  icon?: string | IconType;
  /** 👇 New props */
  showSections?: boolean; // default true
  change?: number; // used when showSections = false
  isPositive?: boolean; // determines arrow and color
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  className,
  titleHeading,
  totalHeading = 0,
  sectionOneTitle,
  sectionOneCount,
  sectionOneTotal,
  sectionTwoTitle,
  sectionTwoCount,
  sectionTwoTotal,
  sectionThreeTitle,
  sectionThreeCount,
  sectionThreeTotal,
  isIcon,
  icon,
  showSections = true,
  change = 0,
  isPositive = true,
}) => {
  const RenderIcon = typeof icon === "function" ? (icon as IconType) : null;

  return (
    <div
      className={`relative overflow-hidden bg-white rounded-lg shadow mx-auto w-[250px] p-3 ${
        className || ""
      } h-fit`}
    >
      {/* --- Header Section --- */}
      <div className="flex gap-x-7 my-2 items-start">
        {isIcon && (
          <div className="p-2 bg-grey100 w-fit h-fit rounded-lg">
            {RenderIcon ? (
              <RenderIcon className="text-primary w-6 h-6" />
            ) : (
              <img
                src={typeof icon === "string" ? icon : WalletIcon}
                alt="icon"
                className="h-6 w-6 object-contain"
              />
            )}
          </div>
        )}

        <div className={`${isIcon ? "" : "mx-auto"} text-center`}>
          <p className="text-grey200 text-sm font-medium mb-1.5">
            {titleHeading}
          </p>
          <p className="text-black500 font-bold text-2xl">
            {totalHeading?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* --- Conditional Content --- */}
      {showSections ? (
        <div>
          {/* --- Section One --- */}
          <div className="flex justify-around items-center p-2 bg-grey100 mb-2 rounded">
            <span className="text-black text-xs">{sectionOneTitle}</span>
            <span className="text-black500 text-xs">{sectionOneCount || 0}</span>
            <div className="flex items-center">
              <img src={TrendUpIcon} alt="trend-up" />
              <p className="text-black500 text-xs">{sectionOneTotal || 0}%</p>
            </div>
          </div>

          {/* --- Section Two --- */}
          <div className="flex justify-around items-center p-2 bg-grey100 mb-2 rounded">
            <span className="text-black text-xs">{sectionTwoTitle}</span>
            <span className="text-black500 text-xs">{sectionTwoCount || 0}</span>
            <div className="flex items-center">
              <img src={TrendDownIcon} alt="trend-down" />
              <p className="text-black500 text-xs">{sectionTwoTotal || 0}%</p>
            </div>
          </div>

          {/* --- Optional Section Three --- */}
          {sectionThreeTitle && (
            <div className="flex justify-around items-center p-2 bg-grey100 rounded">
              <span className="text-black text-xs">{sectionThreeTitle}</span>
              <span className="text-black500 text-xs">
                {sectionThreeCount || 0}
              </span>
              <div className="flex items-center">
                <img src={TrendDownIcon} alt="trend-down" />
                <p className="text-black500 text-xs">
                  {sectionThreeTotal || 0}%
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        // ✅ “Last Period” Summary (same style as section)
        <div
          className={`flex justify-center items-center gap-2 p-2 bg-white rounded mt-2 ${
            isPositive ? "text-green-500" : "text-red-500"
          }`}
        >
          {isPositive ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )}
          <span className="text-xs">{Math.abs(change)}% from last period</span>
        </div>
      )}
    </div>
  );
};

export default SummaryCard;
